import axios from 'axios'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import {
  PeopleContactsResponse,
  PeopleInvitedToOrgResponse,
  UserConnectionResponse,
} from 'src/models/people.models'
import {
  OrganizationMembershipResponse,
  UserListModel,
  UserListResponse,
  UserStatistics,
} from 'src/models/user.models'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

export const GET_PERSON_INIT_ACTION = 'data/people/GET_PERSON_INIT_ACTION'
export const GET_PERSON_SUCCESS_ACTION = 'data/people/GET_PERSON_SUCCESS_ACTION'
export const GET_PERSON_FAILURE_ACTION = 'data/people/GET_PERSON_FAILURE_ACTION'

export type ConnectionsSuccessPayload = PeopleContactsResponse
export type GetPersonSuccessPayload = UserConnectionResponse[]
export type GetUserPayload = UserListModel

export const userConnectionWrapper = (
  user: UserListModel,
): UserConnectionResponse => {
  return {
    id: '',
    createdAt: '',
    reason: '',
    userTo: user,
  }
}

export type GetPersonErrorPayload = BackendErrorPayload<string>

export interface GetPersonInitAction {
  type: typeof GET_PERSON_INIT_ACTION
}
export interface GetPersonSuccessAction {
  type: typeof GET_PERSON_SUCCESS_ACTION
  payload: GetPersonSuccessPayload
}
export interface GetPersonFailureAction {
  type: typeof GET_PERSON_FAILURE_ACTION
  payload: GetPersonErrorPayload
}

export type GetPersonActions =
  | GetPersonInitAction
  | GetPersonSuccessAction
  | GetPersonFailureAction

const createGetPersonInitAction = (): GetPersonInitAction => ({
  type: GET_PERSON_INIT_ACTION,
})
const createGetPersonSuccessAction = (
  payload: GetPersonSuccessPayload,
): GetPersonSuccessAction => ({
  type: GET_PERSON_SUCCESS_ACTION,
  payload,
})
const createGetPersonFailureAction = (
  payload: GetPersonErrorPayload,
): GetPersonFailureAction => ({
  type: GET_PERSON_FAILURE_ACTION,
  payload,
})

export const getPerson = (
  personId: string,
): ThunkAction<void, RootState, void, GetPersonActions> => {
  return (
    dispatch: ThunkDispatch<RootState, void, GetPersonActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetPersonInitAction())
    return axios
      .get<ConnectionsSuccessPayload>(
        `/connections?userToId=${personId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          if (response.data.userConnections.length > 0) {
            dispatch(
              createGetPersonSuccessAction(response.data.userConnections),
            )
          } else {
            axios
              .get<GetUserPayload>(
                `/users/${personId}`,
                selectAuthedJsonApiRequestConfig(getState()),
              )
              .then(
                (response) => {
                  dispatch(
                    createGetPersonSuccessAction([
                      userConnectionWrapper(response.data),
                    ]),
                  )
                },
                (err) => {
                  dispatch(createGetPersonFailureAction(err.response.data))
                },
              )
          }
        },
        (err) => {
          dispatch(createGetPersonFailureAction(err.response.data))
        },
      )
  }
}

export const GET_ORG_MEMBER_INIT = 'data/people/GET_ORG_MEMBER_INIT'
export const GET_ORG_MEMBER_SUCCESS = 'data/people/GET_ORG_MEMBER_SUCCESS'
export const GET_ORG_MEMBER_FAILURE = 'data/people/GET_ORG_MEMBER_FAILURE'

export type GetOrgMemberSuccessPayload = UserListResponse

export type GetOrgMemberErrorPayload = BackendErrorPayload<string>

export interface GetOrgMemberInitAction {
  type: typeof GET_ORG_MEMBER_INIT
}
export interface GetOrgMemberSuccessAction {
  type: typeof GET_ORG_MEMBER_SUCCESS
  payload: GetOrgMemberSuccessPayload
}
export interface GetOrgMemberFailureAction {
  type: typeof GET_ORG_MEMBER_FAILURE
  payload: GetOrgMemberErrorPayload
}

export type GetOrgMemberActions =
  | GetOrgMemberInitAction
  | GetOrgMemberSuccessAction
  | GetOrgMemberFailureAction

const createGetOrgMemberInitAction = (): GetOrgMemberInitAction => ({
  type: GET_ORG_MEMBER_INIT,
})
const createGetOrgMemberSuccessAction = (
  payload: GetOrgMemberSuccessPayload,
): GetOrgMemberSuccessAction => ({
  type: GET_ORG_MEMBER_SUCCESS,
  payload,
})
const createGetOrgMemberFailureAction = (
  payload: GetOrgMemberErrorPayload,
): GetOrgMemberFailureAction => ({
  type: GET_ORG_MEMBER_FAILURE,
  payload,
})

export const getOrgMember = (
  userId: string,
): ThunkAction<void, RootState, void, GetOrgMemberActions> => {
  return (
    dispatch: ThunkDispatch<RootState, void, GetOrgMemberActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetOrgMemberInitAction())
    return axios
      .get<GetOrgMemberSuccessPayload>(
        `/users/${userId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createGetOrgMemberSuccessAction(response.data))
        },
        (err) => {
          dispatch(createGetOrgMemberFailureAction(err.response.data))
        },
      )
  }
}

export const GET_USER_INVITE_INIT_ACTION =
  'data/people/GET_USER_INVITE_INIT_ACTION'
export const GET_USER_INVITE_SUCCESS_ACTION =
  'data/people/GET_USER_INVITE_SUCCESS_ACTION'
export const GET_USER_INVITE_FAILURE_ACTION =
  'data/people/GET_USER_INVITE_FAILURE_ACTION'

export type GetUserInviteSuccessPayload = PeopleInvitedToOrgResponse
export type GetUserInviteFailurePayload = BackendErrorPayload<string>

export type GetUserInviteErrorPayload = BackendErrorPayload<string>

export interface GetUserInviteInitAction {
  type: typeof GET_USER_INVITE_INIT_ACTION
}
export interface GetUserInviteSuccessAction {
  type: typeof GET_USER_INVITE_SUCCESS_ACTION
  payload: GetUserInviteSuccessPayload
}
export interface GetUserInviteFailureAction {
  type: typeof GET_USER_INVITE_FAILURE_ACTION
  payload: GetUserInviteErrorPayload
}

export type GetUserInviteActions =
  | GetUserInviteInitAction
  | GetUserInviteSuccessAction
  | GetUserInviteFailureAction

const createGetUserInviteInitAction = (): GetUserInviteInitAction => ({
  type: GET_USER_INVITE_INIT_ACTION,
})
const createGetUserInviteSuccessAction = (
  payload: GetUserInviteSuccessPayload,
): GetUserInviteSuccessAction => ({
  type: GET_USER_INVITE_SUCCESS_ACTION,
  payload,
})
const createGetUserInviteFailureAction = (
  payload: GetUserInviteErrorPayload,
): GetUserInviteFailureAction => ({
  type: GET_USER_INVITE_FAILURE_ACTION,
  payload,
})

export const getUserInvite = (
  orgId: string,
  inviteeId: string,
): ThunkAction<void, RootState, void, GetUserInviteActions> => {
  return (
    dispatch: ThunkDispatch<RootState, void, GetUserInviteActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetUserInviteInitAction())
    return axios
      .get<GetUserInviteSuccessPayload>(
        `/organizations/${orgId}/invites?inviteeId=${inviteeId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createGetUserInviteSuccessAction(response.data))
        },
        (err) => {
          dispatch(createGetUserInviteFailureAction(err.response.data))
        },
      )
  }
}

export const GET_PERSON_STATISTICS_INIT =
  'data/people/GET_PERSON_STATISTICS_INIT'
export const GET_PERSON_STATISTICS_SUCCESS =
  'data/people/GET_PERSON_STATISTICS_SUCCESS'
export const GET_PERSON_STATISTICS_FAILURE =
  'data/people/GET_PERSON_STATISTICS_FAILURE'

export type GetPersonStatisticsSuccessPayload = UserStatistics

export type GetPersonStatisticsErrorPayload = BackendErrorPayload<string>

export interface GetPersonStatisticsInitAction {
  type: typeof GET_PERSON_STATISTICS_INIT
}
export interface GetPersonStatisticsSuccessAction {
  type: typeof GET_PERSON_STATISTICS_SUCCESS
  payload: GetPersonStatisticsSuccessPayload
  userId: string
}
export interface GetPersonStatisticsFailureAction {
  type: typeof GET_PERSON_STATISTICS_FAILURE
  payload: GetPersonStatisticsErrorPayload
}

export type GetPersonStatisticsActions =
  | GetPersonStatisticsInitAction
  | GetPersonStatisticsSuccessAction
  | GetPersonStatisticsFailureAction

const createGetPersonStatisticsInitAction =
  (): GetPersonStatisticsInitAction => ({
    type: GET_PERSON_STATISTICS_INIT,
  })
const createGetPersonStatisticsSuccessAction = (
  payload: GetPersonStatisticsSuccessPayload,
  userId: string,
): GetPersonStatisticsSuccessAction => ({
  type: GET_PERSON_STATISTICS_SUCCESS,
  payload,
  userId,
})
const createGetPersonStatisticsFailureAction = (
  payload: GetPersonStatisticsErrorPayload,
): GetPersonStatisticsFailureAction => ({
  type: GET_PERSON_STATISTICS_FAILURE,
  payload,
})

export const getPersonStatistics = (
  userId: string,
): ThunkAction<void, RootState, void, GetPersonStatisticsActions> => {
  return (
    dispatch: ThunkDispatch<RootState, void, GetPersonStatisticsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetPersonStatisticsInitAction())
    return axios
      .get<GetPersonStatisticsSuccessPayload>(
        `/statistics/organization?userId=${userId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createGetPersonStatisticsSuccessAction(response.data, userId),
          )
        },
        (err) => {
          dispatch(createGetPersonStatisticsFailureAction(err.response.data))
        },
      )
  }
}

export const GET_ORG_MEMBERSHIP_INIT = 'data/people/GET_ORG_MEMBERSHIP_INIT'
export const GET_ORG_MEMBERSHIP_SUCCESS =
  'data/people/GET_ORG_MEMBERSHIP_SUCCESS'
export const GET_ORG_MEMBERSHIP_FAILURE =
  'data/people/GET_ORG_MEMBERSHIP_FAILURE'

export type GetOrgMembershipSuccessPayload = OrganizationMembershipResponse

export type GetOrgMembershipErrorPayload = BackendErrorPayload<string>

export interface GetOrgMembershipInitAction {
  type: typeof GET_ORG_MEMBERSHIP_INIT
}
export interface GetOrgMembershipSuccessAction {
  type: typeof GET_ORG_MEMBERSHIP_SUCCESS
  payload: GetOrgMembershipSuccessPayload
}
export interface GetOrgMembershipFailureAction {
  type: typeof GET_ORG_MEMBERSHIP_FAILURE
  payload: GetOrgMembershipErrorPayload
}

export type GetOrgMembershipActions =
  | GetOrgMembershipInitAction
  | GetOrgMembershipSuccessAction
  | GetOrgMembershipFailureAction

const createGetOrgMembershipInitAction = (): GetOrgMembershipInitAction => ({
  type: GET_ORG_MEMBERSHIP_INIT,
})
const createGetOrgMembershipSuccessAction = (
  payload: GetOrgMembershipSuccessPayload,
): GetOrgMembershipSuccessAction => ({
  type: GET_ORG_MEMBERSHIP_SUCCESS,
  payload,
})
const createGetOrgMembershipFailureAction = (
  payload: GetOrgMembershipErrorPayload,
): GetOrgMembershipFailureAction => ({
  type: GET_ORG_MEMBERSHIP_FAILURE,
  payload,
})

export const getOrgMembership = (
  userId: string,
  orgId: string,
): ThunkAction<void, RootState, void, GetOrgMembershipActions> => {
  return (
    dispatch: ThunkDispatch<RootState, void, GetOrgMembershipActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetOrgMembershipInitAction())
    return axios
      .get<GetOrgMembershipSuccessPayload>(
        `organizations/${orgId}/members/${userId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createGetOrgMembershipSuccessAction(response.data))
        },
        (err) => {
          dispatch(createGetOrgMembershipFailureAction(err.response.data))
        },
      )
  }
}
